import * as React from 'react';
import { ChevronDown, FileX } from 'lucide-react';

import { EmptyState } from '../ui/empty-state';
import { BarrePagination } from '../ui/pagination-bar';
import { formatDate } from '../../lib/formats';
import { usePagination } from '../../hooks/usePagination';
import { cn } from '../../lib/utils';
import {
  Case,
  CadreTableau,
  EntetesGroupes,
  Ligne,
  MontantCase,
  PastillePrevu,
  Tableau,
} from './TableauRapport';
import {
  BarreEmpilee,
  Chiffre,
  EncartControleRecettes,
  Legende,
  Montant,
  PastilleSignalement,
} from './VisuelsRapport';
import {
  CATEGORIES_CHARGES,
  ENCAISSEMENTS,
  montant,
  nombre,
  partsCharges,
  libelleEffectif,
  pluriel,
  sectionsDeLaPage,
  signalements,
  titreSignalement,
  tranchePartielle,
} from './reportHelpers';

/**
 * Compte d'exploitation du mois.
 *
 * Vue « Par camion » (par défaut): un groupe par flotte (« Propres »,
 * « Donnés en location », chaque transporteur), une rangée par camion avec
 * ses rotations, recettes, charges, résultat et la barre de ses charges par
 * nature. Une rangée se déplie sur la chronologie du mois: seules les
 * opérations qui portent un montant, jamais de case vide.
 *
 * Vue « Tableau » (repli dense): une ligne par opération, mais seulement les
 * colonnes qui ont au moins un montant dans le mois.
 *
 * Les deux vues se lisent par pages (20, 50 ou 100 lignes): la liste est celle
 * de toute la flotte, groupe après groupe. Un groupe coupé entre deux pages
 * reparaît en tête de la suivante avec son titre et son sous-total, qui restent
 * ceux du groupe entier; le TOTAL du mois reste affiché sous la liste. Tous les
 * totaux viennent du serveur, jamais de la page.
 */

export const GROUPE_PROPRES = 'Propres';
export const GROUPE_DONNES = 'Donnés en location';
export const GROUPE_NON_REPARTI = 'Charges de convoi non réparties';

/** Famille d'un groupe de flotte, pour sa pastille. */
export const familleGroupe = (libelle) => {
  if (libelle === GROUPE_PROPRES) return null;
  if (libelle === GROUPE_DONNES) return 'Location sortante';
  if (libelle === GROUPE_NON_REPARTI) return 'Sans camion';
  return 'Pris en location';
};

const CLES_SOMMEES = [
  'revenue',
  'total_revenue',
  'total_cost',
  'result',
  ...ENCAISSEMENTS.map((e) => e.key),
  ...CATEGORIES_CHARGES.map((c) => c.key),
];

const arrondi = (n) => Math.round(n * 100) / 100;

const observation = (ligne) => (ligne.notes || []).filter(Boolean).map(String).join(' ; ');

/** La ligne porte-t-elle quelque chose à montrer dans la chronologie? */
const ligneUtile = (ligne) =>
  CLES_SOMMEES.some((cle) => cle !== 'result' && montant(ligne[cle]) !== 0) ||
  Number(ligne.rotations) > 0 ||
  signalements(ligne).length > 0;

/** Les camions d'un groupe, avec leurs totaux et leurs opérations. */
export const camionsDuGroupe = (groupe) => {
  const parCamion = new Map();
  (groupe.lines || []).forEach((ligne) => {
    const cle = ligne.truck_id || ligne.tractor_plate || 'sans-camion';
    if (!parCamion.has(cle)) {
      parCamion.set(cle, {
        cle,
        tractor_plate: ligne.tractor_plate || '',
        tank_plate: ligne.tank_plate || '',
        driver_name: '',
        rotations: 0,
        estimated: false,
        totaux: Object.fromEntries(CLES_SOMMEES.map((k) => [k, 0])),
        lignes: [],
        signalements: [],
        titres: {},
      });
    }
    const camion = parCamion.get(cle);
    if (!camion.tank_plate && ligne.tank_plate) camion.tank_plate = ligne.tank_plate;
    if (ligne.driver_name) camion.driver_name = ligne.driver_name;
    camion.rotations += Number(ligne.rotations) || 0;
    camion.estimated = camion.estimated || Boolean(ligne.estimated);
    CLES_SOMMEES.forEach((k) => {
      camion.totaux[k] = arrondi(camion.totaux[k] + montant(ligne[k]));
    });
    signalements(ligne).forEach((texte) => {
      if (!camion.signalements.includes(texte)) {
        camion.signalements.push(texte);
        camion.titres[texte] = titreSignalement(ligne, texte);
      }
    });
    if (ligneUtile(ligne)) camion.lignes.push(ligne);
  });
  return Array.from(parCamion.values()).sort((a, b) =>
    a.tractor_plate.localeCompare(b.tractor_plate, 'fr'),
  );
};

/** Colonnes du tableau qui ont au moins un montant (les colonnes d'identité restent). */
export const colonnesNonVides = (colonnes, lignes, totaux) => {
  const garder = new Set(['date', 'tractor_plate', 'tank_plate']);
  return colonnes.filter((colonne) => {
    if (garder.has(colonne.key)) return true;
    if (colonne.key === 'notes') return lignes.some((l) => observation(l) || l.estimated);
    if (colonne.key === 'rotations') return lignes.some((l) => Number(l.rotations) > 0);
    return montant(totaux?.[colonne.key]) !== 0 || lignes.some((l) => montant(l[colonne.key]) !== 0);
  });
};

/* ------------------------------------------------------------------------ */
/* Vue par camion                                                            */
/* ------------------------------------------------------------------------ */

/** Grille commune de l'en-tête, des rangées et des sous-totaux (grand écran). */
const GRILLE =
  'xl:grid xl:grid-cols-[minmax(13rem,1.5fr)_4.5rem_repeat(3,minmax(7rem,1fr))_minmax(9rem,1.3fr)_1.25rem] xl:items-center xl:gap-x-4';

const EnteteColonnes = () => (
  <div
    className={cn(
      'hidden border-b border-border/70 px-4 py-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground',
      GRILLE,
    )}
    aria-hidden="true"
  >
    <span>Camion</span>
    <span className="text-right">Rotations</span>
    <span className="text-right">Recettes</span>
    <span className="text-right">Charges</span>
    <span className="text-right">Résultat</span>
    <span>Charges par nature</span>
    <span />
  </div>
);

/** Ce que porte une opération: recette, charges, encaissements. */
const montantsLigne = (ligne) => {
  const liste = [];
  if (montant(ligne.revenue)) liste.push({ key: 'revenue', label: 'Facture transport', valeur: montant(ligne.revenue) });
  CATEGORIES_CHARGES.forEach((c) => {
    if (montant(ligne[c.key])) liste.push({ key: c.key, label: c.label, valeur: montant(ligne[c.key]), couleur: c.couleur });
  });
  ENCAISSEMENTS.forEach((e) => {
    if (montant(ligne[e.key])) liste.push({ key: e.key, label: e.label, valeur: montant(ligne[e.key]), info: true });
  });
  return liste;
};

const natureLigne = (ligne, montants) => {
  if (ligne.label) return ligne.label;
  if (Number(ligne.rotations) > 0) return 'Rotation';
  return montants[0]?.label || 'Opération';
};

const Chronologie = ({ camion }) => {
  if (camion.lignes.length === 0) {
    return <p className="px-4 py-3 text-sm text-muted-foreground">Aucune opération chiffrée ce mois.</p>;
  }
  return (
    <ol className="divide-y divide-border/60" aria-label={`Opérations du mois de ${camion.tractor_plate}`}>
      {camion.lignes.map((ligne, index) => {
        const montants = montantsLigne(ligne);
        const nature = natureLigne(ligne, montants);
        const detail = montants.length > 1 || montants.some((m) => m.info);
        const obs = observation(ligne);
        const alertes = signalements(ligne);
        const effet = montant(ligne.result);
        return (
          <li
            key={`${ligne.date}-${index}`}
            className="grid grid-cols-[4.5rem_1fr_auto] items-start gap-x-3 px-4 py-2.5 text-sm sm:grid-cols-[5.5rem_1fr_auto]"
          >
            <span className="tabular-nums text-muted-foreground">{formatDate(ligne.date, '')}</span>
            <div className="min-w-0 space-y-1">
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
                  {montants.length === 1 && montants[0].couleur && (
                    <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: montants[0].couleur }} aria-hidden="true" />
                  )}
                  {nature}
                </span>
                {ligne.estimated && <PastillePrevu />}
                {alertes.map((texte) => (
                  <PastilleSignalement key={texte} texte={texte} titre={titreSignalement(ligne, texte)} />
                ))}
              </p>
              {detail && (
                <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                  {montants.map((m) => (
                    <li key={m.key} className="inline-flex items-center gap-1">
                      {m.couleur && (
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: m.couleur }} aria-hidden="true" />
                      )}
                      {m.label}
                      <span className="tabular-nums text-foreground">{nombre(m.valeur)}</span>
                      {m.info && <span>(encaissé)</span>}
                    </li>
                  ))}
                </ul>
              )}
              {obs && <p className="text-xs text-muted-foreground">{obs}</p>}
            </div>
            <span className="text-right">
              {montants.length === 0 ? (
                <span className="text-xs text-muted-foreground">Aucun montant</span>
              ) : effet !== 0 ? (
                <Montant valeur={effet} signe />
              ) : (
                <Montant valeur={montants[0].valeur} className="text-muted-foreground" />
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

const RangeeCamion = ({ camion, ouvert, onBasculer, max }) => {
  const parts = partsCharges(camion.totaux);
  const idDetail = `detail-${camion.cle}`;
  const recettes = camion.totaux.total_revenue;
  const charges = camion.totaux.total_cost;
  return (
    <li className="border-b border-border/60 last:border-b-0">
      <button
        type="button"
        onClick={onBasculer}
        aria-expanded={ouvert}
        aria-controls={idDetail}
        className={cn(
          'w-full px-4 py-3 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          ouvert && 'bg-muted/30',
          GRILLE,
        )}
      >
        {/* Identité */}
        <span className="flex min-w-0 items-start justify-between gap-3 xl:block">
          <span className="min-w-0">
            <span className="block truncate font-semibold text-foreground">
              {camion.tractor_plate || 'Camion sans plaque'}
              {camion.tank_plate && (
                <span className="font-normal text-muted-foreground"> · {camion.tank_plate}</span>
              )}
            </span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
              {camion.driver_name && <span className="truncate">{camion.driver_name}</span>}
              {camion.estimated && <PastillePrevu />}
              {camion.signalements.map((texte) => (
                <PastilleSignalement key={texte} texte={texte} titre={camion.titres[texte]} />
              ))}
            </span>
          </span>
          {/* Petit écran: résultat à droite de l'identité */}
          <span className="shrink-0 text-right xl:hidden">
            <Montant valeur={camion.totaux.result} signe fort />
            <ChevronDown
              className={cn('ml-2 inline h-4 w-4 text-muted-foreground transition-transform', ouvert && 'rotate-180')}
              aria-hidden="true"
            />
          </span>
        </span>

        {/* Grand écran: colonnes alignées */}
        <span className="hidden text-right tabular-nums xl:block">{camion.rotations || '—'}</span>
        <span className="hidden text-right xl:block">
          <Montant valeur={recettes} />
        </span>
        <span className="hidden text-right xl:block">
          <Montant valeur={charges} />
        </span>
        <span className="hidden text-right xl:block">
          <Montant valeur={camion.totaux.result} signe fort />
        </span>

        <span className="mt-2 block xl:mt-0">
          {parts.length === 0 ? (
            <span className="text-xs text-muted-foreground">Aucune charge</span>
          ) : (
          <BarreEmpilee
            parts={parts}
            total={max}
            libelle={
              parts.length
                ? `Charges de ${camion.tractor_plate} : ${parts.map((p) => `${p.label} ${nombre(p.valeur)}`).join(', ')}`
                : `Aucune charge pour ${camion.tractor_plate}`
            }
          />
          )}
        </span>

        {/* Petit écran: les trois chiffres sous la barre */}
        <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground xl:hidden">
          <span>
            {camion.rotations} {pluriel(camion.rotations, 'rotation', 'rotations')}
          </span>
          <span>
            Recettes <Montant valeur={recettes} className="text-foreground" />
          </span>
          <span>
            Charges <Montant valeur={charges} className="text-foreground" />
          </span>
        </span>

        <ChevronDown
          className={cn(
            'hidden h-4 w-4 justify-self-end text-muted-foreground transition-transform xl:block',
            ouvert && 'rotate-180',
          )}
          aria-hidden="true"
        />
        <span className="sr-only">{ouvert ? 'Masquer les opérations' : 'Voir les opérations du mois'}</span>
      </button>
      {ouvert && (
        <div id={idDetail} className="border-t border-border/60 bg-muted/20">
          <Chronologie camion={camion} />
        </div>
      )}
    </li>
  );
};

const LigneSousTotal = ({ libelle, totaux, rotations, ton = 'sousTotal' }) => (
  <div
    className={cn(
      'grid grid-cols-3 items-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-sm',
      ton === 'total' ? 'bg-card font-semibold' : 'border-t border-border bg-muted/40 font-semibold',
      GRILLE,
    )}
  >
    <span className="col-span-3 xl:col-span-1">{libelle}</span>
    <span className="hidden text-right tabular-nums xl:block">{rotations ?? 0}</span>
    <Chiffre label="Recettes" valeur={totaux?.total_revenue} className="xl:hidden" />
    <span className="hidden text-right xl:block">
      <Montant valeur={totaux?.total_revenue} />
    </span>
    <Chiffre label="Charges" valeur={totaux?.total_cost} className="xl:hidden" />
    <span className="hidden text-right xl:block">
      <Montant valeur={totaux?.total_cost} />
    </span>
    <Chiffre label="Résultat" valeur={totaux?.result} signe fort className="xl:hidden" />
    <span className="hidden text-right xl:block">
      <Montant valeur={totaux?.result} signe fort />
    </span>
    <span className="hidden xl:block" />
    <span className="hidden xl:block" />
  </div>
);

const SectionGroupe = ({ groupe, camions, nbCamions, partiel, ouverts, basculer, max }) => {
  const famille = familleGroupe(groupe.label);
  const nb = groupe.trucks_count || nbCamions;
  const idTitre = `groupe-${groupe.label}`.replace(/\s+/g, '-');
  return (
    <section aria-labelledby={idTitre} className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-border/70 px-4 py-2.5">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h3 id={idTitre} className="text-sm font-semibold text-foreground">
            {groupe.label}
          </h3>
          {famille && (
            <span className="rounded-full border border-border px-2 py-px text-[11px] text-muted-foreground">{famille}</span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {partiel && <span className="font-medium text-foreground">{partiel} · </span>}
          {libelleEffectif(groupe) ? `${libelleEffectif(groupe)} · ` : nb > 0 && `${nb} ${pluriel(nb, 'camion', 'camions')} · `}
          {groupe.rotations ?? 0} {pluriel(groupe.rotations, 'rotation', 'rotations')}
        </p>
      </header>
      <EnteteColonnes />
      <ul>
        {camions.map((camion) => (
          <RangeeCamion
            key={camion.cle}
            camion={camion}
            max={max}
            ouvert={ouverts.has(`${groupe.label}|${camion.cle}`)}
            onBasculer={() => basculer(`${groupe.label}|${camion.cle}`)}
          />
        ))}
      </ul>
      <LigneSousTotal libelle={`Sous-total ${groupe.label}`} totaux={groupe.totals} rotations={groupe.rotations} />
    </section>
  );
};

const VueParCamion = ({ report, groupes, cleRapport }) => {
  const [ouverts, setOuverts] = React.useState(() => new Set());
  const basculer = (cle) =>
    setOuverts((precedent) => {
      const suivant = new Set(precedent);
      if (suivant.has(cle)) suivant.delete(cle);
      else suivant.add(cle);
      return suivant;
    });

  const sections = React.useMemo(
    () => groupes.map((groupe) => ({ groupe, camions: camionsDuGroupe(groupe) })),
    [groupes],
  );
  // La liste à plat de toute la flotte, groupe après groupe: c'est elle qu'on pagine.
  const entrees = React.useMemo(
    () => sections.flatMap(({ groupe, camions }) => camions.map((camion, rang) => ({ section: groupe.label, rang, camion }))),
    [sections],
  );
  const pagination = usePagination(entrees, { cle: 'exploitation', reinitialiser: cleRapport });
  // Une même échelle pour toutes les barres: on compare les camions d'un coup d'œil.
  const max = Math.max(
    1,
    ...sections.flatMap(({ camions }) => camions.map((c) => partsCharges(c.totaux).reduce((s, p) => s + p.valeur, 0))),
  );
  const partsMois = partsCharges(report.totals);
  const tousOuverts = sections.every(({ groupe, camions }) => camions.every((c) => ouverts.has(`${groupe.label}|${c.cle}`)));
  const toutBasculer = () =>
    setOuverts(
      tousOuverts
        ? new Set()
        : new Set(sections.flatMap(({ groupe, camions }) => camions.map((c) => `${groupe.label}|${c.cle}`))),
    );

  return (
    <div className="space-y-4">
      {partsMois.length > 0 && (
        <section aria-label="Charges du mois par nature" className="space-y-2 rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
            <h3 className="text-sm font-semibold text-foreground">Charges du mois par nature</h3>
            <button
              type="button"
              onClick={toutBasculer}
              className="text-xs font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {tousOuverts ? 'Replier tous les camions' : 'Déplier tous les camions'}
            </button>
          </div>
          <BarreEmpilee parts={partsMois} hauteur="h-3" />
          <Legende parts={partsMois} />
        </section>
      )}

      {sectionsDeLaPage(pagination.lignes).map(({ cle, entrees: tranche }) => {
        const { groupe, camions } = sections.find((section) => section.groupe.label === cle);
        return (
          <SectionGroupe
            key={cle}
            groupe={groupe}
            camions={tranche.map((entree) => entree.camion)}
            nbCamions={camions.filter((c) => c.cle !== 'convoi-non-reparti').length}
            partiel={tranchePartielle(tranche, camions.length, 'camions')}
            ouverts={ouverts}
            basculer={basculer}
            max={max}
          />
        );
      })}

      <BarrePagination pagination={pagination} nom="camions" className="mt-0" />

      <section aria-label="Total du mois" className="overflow-hidden rounded-xl border-2 border-foreground/15 bg-card shadow-sm">
        {groupes.length > 1 && (
          <ul className="divide-y divide-border/60 text-sm">
            {groupes.map((groupe) => (
              <li key={groupe.label} className={cn('px-4 py-2 text-muted-foreground', GRILLE, 'flex flex-wrap items-center justify-between gap-x-6')}>
                <span className="text-foreground">{groupe.label}</span>
                <span className="hidden text-right tabular-nums xl:block">{groupe.rotations ?? 0}</span>
                <span className="hidden text-right xl:block"><Montant valeur={groupe.totals?.total_revenue} /></span>
                <span className="hidden text-right xl:block"><Montant valeur={groupe.totals?.total_cost} /></span>
                <span className="text-right xl:block"><Montant valeur={groupe.totals?.result} signe /></span>
                <span className="hidden xl:block" />
                <span className="hidden xl:block" />
              </li>
            ))}
          </ul>
        )}
        <LigneSousTotal libelle="TOTAL du mois" totaux={report.totals} rotations={report.totals?.rotations} ton="total" />
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------------ */
/* Vue tableau (repli dense)                                                 */
/* ------------------------------------------------------------------------ */

const COLONNES_TOTAUX = new Set(['total_revenue', 'total_operating', 'total_maintenance', 'total_cost', 'result']);
const COLONNES_IDENTITE = ['date', 'tractor_plate', 'tank_plate'];

const largeurLibelle = (colonnes) => {
  let n = 0;
  while (n < colonnes.length && COLONNES_IDENTITE.includes(colonnes[n].key)) n += 1;
  return Math.max(1, n);
};

const CaseDetail = ({ colonne, ligne }) => {
  const { key, align } = colonne;
  if (key === 'date') return <Case align={align}>{formatDate(ligne.date, '')}</Case>;
  if (key === 'tractor_plate' || key === 'tank_plate') return <Case align={align}>{ligne[key] || ''}</Case>;
  if (key === 'rotations') return <Case align={align}>{ligne.rotations ? 1 : ''}</Case>;
  if (key === 'notes') {
    return (
      <Case align={align} className="min-w-[12rem] whitespace-normal text-xs text-muted-foreground">
        {observation(ligne)}
        {ligne.estimated && <PastillePrevu className={observation(ligne) ? 'ml-2' : undefined} />}
        {signalements(ligne).map((texte) => (
          <PastilleSignalement key={texte} texte={texte} titre={titreSignalement(ligne, texte)} className="ml-2" />
        ))}
      </Case>
    );
  }
  return <MontantCase valeur={ligne[key]} fort={COLONNES_TOTAUX.has(key)} resultat={key === 'result'} />;
};

const LigneTotaux = ({ colonnes, libelle, totaux, ton }) => {
  const largeur = largeurLibelle(colonnes);
  return (
    <Ligne ton={ton}>
      <Case colSpan={largeur} className="font-semibold">
        {libelle}
      </Case>
      {colonnes.slice(largeur).map((colonne) => {
        if (colonne.key === 'notes') return <Case key={colonne.key} />;
        if (colonne.key === 'rotations') {
          return (
            <Case key={colonne.key} align="center" fort>
              {totaux?.rotations ?? 0}
            </Case>
          );
        }
        return (
          <MontantCase key={colonne.key} valeur={totaux?.[colonne.key] ?? 0} total fort resultat={colonne.key === 'result'} />
        );
      })}
    </Ligne>
  );
};

const VueTableau = ({ report, groupes, cleRapport }) => {
  const toutesLignes = React.useMemo(() => groupes.flatMap((g) => g.lines || []), [groupes]);
  // Les colonnes se décident sur tout le mois, pas sur la page: elles ne bougent pas d'une page à l'autre.
  const colonnes = React.useMemo(
    () => colonnesNonVides(report.columns || [], toutesLignes, report.totals),
    [report, toutesLignes],
  );
  // Un groupe sans aucune opération chiffrée n'a rien à montrer: ses totaux sont à zéro.
  const actifs = React.useMemo(() => groupes.filter((g) => (g.lines || []).some(ligneUtile)), [groupes]);
  const sansChiffre = groupes.filter((g) => !actifs.includes(g));
  const parGroupe = React.useMemo(
    () => new Map(actifs.map((groupe) => [groupe.label, { groupe, lignes: (groupe.lines || []).filter(ligneUtile) }])),
    [actifs],
  );
  const entrees = React.useMemo(
    () =>
      Array.from(parGroupe.values()).flatMap(({ groupe, lignes }) =>
        lignes.map((ligne, rang) => ({ section: groupe.label, rang, ligne })),
      ),
    [parGroupe],
  );
  const pagination = usePagination(entrees, { cle: 'operations', reinitialiser: cleRapport });
  return (
    <div className="space-y-5">
      {sectionsDeLaPage(pagination.lignes).map(({ cle, entrees: tranche }) => {
        const { groupe, lignes } = parGroupe.get(cle);
        const partiel = tranchePartielle(tranche, lignes.length, 'opérations');
        return (
          <CadreTableau
            key={cle}
            titre={groupe.label}
            detail={[
              partiel,
              libelleEffectif(groupe) || `${groupe.trucks_count ?? 0} ${pluriel(groupe.trucks_count, 'camion', 'camions')}`,
              `${groupe.rotations ?? 0} ${pluriel(groupe.rotations, 'rotation', 'rotations')}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <Tableau>
              <EntetesGroupes colonnes={colonnes} />
              <tbody>
                {tranche.map(({ ligne, rang }) => (
                  <Ligne key={`${ligne.date}-${ligne.tractor_plate}-${rang}`}>
                    {colonnes.map((colonne) => (
                      <CaseDetail key={colonne.key} colonne={colonne} ligne={ligne} />
                    ))}
                  </Ligne>
                ))}
                <LigneTotaux colonnes={colonnes} libelle={`Sous-total ${groupe.label}`} totaux={groupe.totals} ton="sousTotal" />
              </tbody>
            </Tableau>
          </CadreTableau>
        );
      })}
      <BarrePagination pagination={pagination} nom="opérations" className="mt-0" />
      {sansChiffre.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Sans opération chiffrée ce mois : {sansChiffre.map((g) => g.label).join(', ')}.
        </p>
      )}
      <CadreTableau>
        <Tableau>
          <EntetesGroupes colonnes={colonnes} />
          <tbody>
            <LigneTotaux colonnes={colonnes} libelle="TOTAL du mois" totaux={report.totals} ton="total" />
          </tbody>
        </Tableau>
      </CadreTableau>
    </div>
  );
};

/* ------------------------------------------------------------------------ */

const OperatingAccountReport = ({ report, disposition = 'camions' }) => {
  const tous = React.useMemo(() => report?.groups || [], [report]);
  const groupes = React.useMemo(() => tous.filter((groupe) => (groupe.lines || []).length > 0), [tous]);
  const sansOperation = tous.filter((groupe) => (groupe.lines || []).length === 0);
  // Un autre mois ou un autre filtre ramène à la première page.
  const cleRapport = `${report?.period?.label || ''}|${report?.filters?.truck_id || ''}|${report?.filters?.company_id || ''}`;

  if (!report || groupes.length === 0) {
    return (
      <EmptyState
        icon={FileX}
        title="Aucune rotation ce mois-ci"
        description={`Aucune rotation ni aucune charge n'est enregistrée pour ${report?.period?.label || 'ce mois'}${
          report?.filters?.truck_label || report?.filters?.company_label ? ' avec ce filtre' : ''
        }.`}
      >
        Choisissez un autre mois avec les flèches, ou retirez le filtre.
      </EmptyState>
    );
  }

  return (
    <div className="space-y-4">
      <EncartControleRecettes rapport={report} />
      {disposition === 'tableau' ? (
        <VueTableau report={report} groupes={groupes} cleRapport={cleRapport} />
      ) : (
        <VueParCamion report={report} groupes={groupes} cleRapport={cleRapport} />
      )}
      {sansOperation.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Sans opération ce mois : {sansOperation.map((g) => g.label).join(', ')}.
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {report.estimated && 'Les montants marqués « Prévu » sont estimés, pas encore payés. '}
        Les avances et paiements reçus sont des encaissements : ils ne s'ajoutent pas aux recettes, déjà comptées par la facture de transport.
      </p>
    </div>
  );
};

export default OperatingAccountReport;
